import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import api from './authService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Handle API errors
 */
const handleError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { data } = error.response;
    return {
      message: data.message || 'An error occurred',
      errors: data.errors || {},
      status: error.response.status,
    };
  } else if (error.request) {
    // Network error
    return {
      message: 'Network error. Please check your connection.',
      status: 0,
    };
  } else {
    // Other error
    return {
      message: error.message || 'An unexpected error occurred',
      status: 500,
    };
  }
};

/**
 * Get all available subscription plans (public - for vendors)
 */
export const getAvailablePlans = async () => {
  try {
    const response = await api.get('/subscriptions');
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get all subscription plans (admin - for admin management)
 */
export const getAdminSubscriptions = async (params = {}) => {
  try {
    const response = await api.get('/admin/subscriptions', { params });
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get vendor's subscription status
 */
export const getVendorStatus = async () => {
  try {
    const response = await api.get('/vendor/subscription/status');
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/** Single React Query key for all vendor subscription / plan-access reads */
export const vendorSubscriptionKeys = {
  all: ['vendor', 'subscription-status'],
  status: () => [...vendorSubscriptionKeys.all],
};

export const VENDOR_SUBSCRIPTION_STALE_MS = 2 * 60 * 1000;

/**
 * Canonical hook — all vendor plan/feature checks should use this (dedupes network calls).
 * Never call the vendor endpoint for non-vendors (admin/client get 403).
 */
export const useVendorSubscriptionStatus = (options = {}) => {
  const { user } = useAuth();
  const { enabled = true, ...rest } = options;
  const isVendor = user?.user_type === 'vendor';

  return useQuery({
    queryKey: vendorSubscriptionKeys.status(),
    queryFn: getVendorStatus,
    staleTime: VENDOR_SUBSCRIPTION_STALE_MS,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    ...rest,
    enabled: Boolean(enabled) && isVendor,
  });
};

/** Derived subscription data + optional feature flag check */
export const useVendorSubscriptionFeatures = (featureKey = null, options = {}) => {
  const query = useVendorSubscriptionStatus(options);
  const payload = query.data?.data;
  const features = payload?.current_subscription?.features ?? {};

  return {
    ...query,
    isChecking: query.isLoading,
    hasActiveSubscription: payload?.has_active_subscription ?? null,
    currentSubscription: payload?.current_subscription ?? null,
    features,
    limits: payload?.current_subscription?.limits ?? {},
    usage: payload?.current_subscription?.usage ?? {},
    hasFeature: featureKey ? !!features[featureKey] : null,
    checkSubscriptionStatus: () => query.refetch(),
    refreshSubscriptionStatus: () => query.refetch(),
  };
};

export const invalidateVendorSubscriptionStatus = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: vendorSubscriptionKeys.all });
};

/**
 * Create a Stripe payment intent for subscription
 */
export const createSubscriptionPaymentIntent = async (data) => {
  try {
    const response = await api.post('/vendor/subscription/payment-intent', data);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Confirm Stripe subscription payment
 */
export const confirmSubscriptionPayment = async (data) => {
  try {
    const response = await api.post('/vendor/subscription/confirm-payment', data);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Subscribe to a plan
 */
export const subscribe = async (subscriptionData) => {
  try {
    const response = await api.post('/vendor/subscription/subscribe', subscriptionData);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Renew current subscription
 */
export const renewSubscription = async (renewalData) => {
  try {
    const response = await api.post('/vendor/subscription/renew', renewalData);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Cancel current subscription
 */
export const cancelSubscription = async () => {
  try {
    const response = await api.post('/vendor/subscription/cancel');
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export const updateVendorSubscriptionAutoRenew = async (autoRenew) => {
  try {
    const response = await api.patch('/vendor/subscription/auto-renew', {
      auto_renew: !!autoRenew,
    });
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get subscription history
 */
export const getSubscriptionHistory = async () => {
  try {
    const response = await api.get('/vendor/subscription/history');
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Create a new subscription plan (admin only)
 */
export const createSubscription = async (subscriptionData) => {
  try {
    const response = await api.post('/admin/subscriptions', subscriptionData);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Update a subscription plan (admin only)
 */
export const updateSubscription = async (id, subscriptionData) => {
  try {
    const response = await api.put(`/admin/subscriptions/${id}`, subscriptionData);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Delete a subscription plan (admin only)
 */
export const deleteSubscription = async (id) => {
  try {
    const response = await api.delete(`/admin/subscriptions/${id}`);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Toggle subscription status (admin only)
 */
export const toggleSubscriptionStatus = async (id, isActive) => {
  try {
    const response = await api.patch(`/admin/subscriptions/${id}/status`, {
      is_active: isActive
    });
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export const getAdminSubscriptionPayments = async (params = {}) => {
  try {
    const response = await api.get('/admin/subscription-payments', { params });
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

export const deleteAdminSubscriptionPayment = async (id) => {
  try {
    const response = await api.delete(`/admin/subscription-payments/${id}`);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

// Service object for backward compatibility
const subscriptionService = {
  getAvailablePlans,
  getAdminSubscriptions,
  getVendorStatus,
  subscribe,
  createSubscriptionPaymentIntent,
  confirmSubscriptionPayment,
  renewSubscription,
  cancelSubscription,
  updateVendorSubscriptionAutoRenew,
  getSubscriptionHistory,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  toggleSubscriptionStatus,
  getAdminSubscriptionPayments,
  deleteAdminSubscriptionPayment,
};

// React Query hooks for admin subscriptions
export const useSubscriptions = (params = {}) => {
  return useQuery({
    queryKey: ['admin-subscriptions', params],
    queryFn: () => getAdminSubscriptions(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// React Query hook for public subscription plans (for vendors)
export const useAvailablePlans = () => {
  return useQuery({
    queryKey: ['available-plans'],
    queryFn: () => getAvailablePlans(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to get a specific plan by ID
export const usePlanById = (planId) => {
  const { data: plansData, isLoading, error } = useAvailablePlans();
  
  const plan = useMemo(() => {
    if (!planId || !plansData?.data) return null;
    return plansData.data.find(p => p.id === planId);
  }, [planId, plansData?.data]);

  return {
    plan,
    isLoading,
    error,
    isFound: !!plan
  };
};

export const useSubscriptionStats = () => {
  return useQuery({
    queryKey: ['subscription-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/subscriptions/stats');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateSubscription = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionData) => createSubscription(subscriptionData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-stats'] });
      onSuccess?.(data);
    },
    onError,
  });
};

export const useUpdateSubscription = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, ...subscriptionData }) => updateSubscription(id, subscriptionData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-stats'] });
      onSuccess?.(data);
    },
    onError,
  });
};

export const useDeleteSubscription = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => deleteSubscription(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-stats'] });
      onSuccess?.(data);
    },
    onError,
  });
};

export const useToggleSubscriptionStatus = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, isActive }) => toggleSubscriptionStatus(id, isActive),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-stats'] });
      onSuccess?.(data);
    },
    onError,
  });
};

export default subscriptionService;